import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { capitalize } from 'lodash';
import { format } from 'date-fns';
import CwFPagination from 'components/CwFPagination';
import LoadingSpinner from 'components/LoadingSpinner';
import Paper from 'components/Paper';
import LandingList from 'components/LandingList';
import SortableTable from 'components/UiKit/SortableTable';
import { paths } from 'routes/paths';
import './index.css';

export const TABLE_COLUMNS = [
  {
    field: 'name',
    title: 'Name',
    sortable: true
  },
  {
    field: 'email',
    title: 'Email',
    sortable: true
  },
  {
    field: 'license',
    title: 'License',
    sortable: true
  },
  {
    field: 'expirationDate',
    title: 'Expiration Date',
    sortable: true
  },
  {
    field: 'permission',
    title: 'Permission',
    sortable: false
  }
];

const PEOPLE_TABLE_BOTTOM_GAP = 16;
const PAPER_CONTENT_BOTTOM_SPACE = 25;

const formatExpirationDate = ({ applicatorLicenseExpirationDateEpoch }) => {
  const expDate = Date.parse(new Date(applicatorLicenseExpirationDateEpoch));
  return expDate ? format(expDate, 'MMM d, yyyy') : '';
};

const PersonNameCell = ({ user, onAction }) => (
  <button
    type="button"
    className="font-bold text-left"
    onClick={() => onAction('view', user)}
  >
    {user.name}
  </button>
);

PersonNameCell.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string
  }).isRequired,
  onAction: PropTypes.func.isRequired
};

const PersonPermissionCell = ({ user, getMenu }) => (
  <div className="relative flex items-center">
    {capitalize(user.permissionLevel)}
    <div className="absolute right-0 flex-none w-10">{getMenu(user)}</div>
  </div>
);

PersonPermissionCell.propTypes = {
  user: PropTypes.shape({
    permissionLevel: PropTypes.string
  }).isRequired,
  getMenu: PropTypes.func.isRequired
};

const peopleListDefaultPropPeople = [];
const PeopleList = ({
  people = peopleListDefaultPropPeople,
  isLoading = false,
  onAction: handleAction,
  getMenu,
  totalPeople,
  peoplePageNumber,
  handlePageChange,
  sortBy,
  sortDir,
  onSort
}) => {
  const tableContainerRef = useRef(null);
  const [tableContainerHeight, setTableContainerHeight] = useState(
    window.innerHeight
  );
  const [tableBodyHeight, setTableBodyHeight] = useState(520);

  useLayoutEffect(() => {
    const updateTableDimensions = () => {
      const container = tableContainerRef.current;
      const paper = container?.firstElementChild;
      if (!container || !paper) {
        return;
      }

      const containerTop = container.getBoundingClientRect().top;
      const nextContainerHeight = Math.max(
        0,
        window.innerHeight - containerTop - PEOPLE_TABLE_BOTTOM_GAP
      );
      setTableContainerHeight(current =>
        current === nextContainerHeight ? current : nextContainerHeight
      );

      const tableBody = paper.querySelector('.syt-antd-table-body');
      const footerPagination = paper.lastElementChild;
      if (tableBody && footerPagination) {
        const nextTableBodyHeight = Math.max(
          0,
          containerTop +
            nextContainerHeight -
            PAPER_CONTENT_BOTTOM_SPACE -
            footerPagination.getBoundingClientRect().height -
            tableBody.getBoundingClientRect().top
        );
        setTableBodyHeight(current =>
          current === nextTableBodyHeight ? current : nextTableBodyHeight
        );
      }
    };

    updateTableDimensions();
    window.addEventListener('resize', updateTableDimensions);
    return () => window.removeEventListener('resize', updateTableDimensions);
  });

  const renderListContent = () => {
    if (isLoading || people.length) {
      return (
        <div
          className={`people-list-table relative min-w-0 overflow-hidden ${
            isLoading ? 'people-list-table--loading' : ''
          } ${isLoading ? 'min-h-0 flex-1' : ''}`}
          aria-busy={isLoading}
        >
          <SortableTable
            allowUnsort
            columns={TABLE_COLUMNS}
            rows={isLoading ? [] : people}
            renderCell={(field, user) => {
              switch (field) {
                case 'name':
                  return <PersonNameCell user={user} onAction={handleAction} />;
                case 'email':
                  return user.email;
                case 'license':
                  return user.applicatorLicenseNumber;
                case 'expirationDate':
                  return formatExpirationDate(user);
                case 'permission':
                  return <PersonPermissionCell user={user} getMenu={getMenu} />;
                default:
                  return null;
              }
            }}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
            scrollY={tableBodyHeight}
            data-testid="people-table"
          />
          {isLoading && (
            <div
              className="people-list-table__loading z-10 flex items-center justify-center bg-white"
              data-testid="people-list-loading"
              aria-busy="true"
            >
              <LoadingSpinner message="" size={72} label="Loading people" />
            </div>
          )}
        </div>
      );
    }

    return (
      <LandingList.LandingEmpty
        listType="People"
        routePath={`${paths.people}/person`}
      />
    );
  };

  return (
    <div
      ref={tableContainerRef}
      className="w-full"
      style={isLoading ? { height: tableContainerHeight } : undefined}
      data-testid="people-table-container"
    >
      <Paper className="!mt-0 flex h-full min-h-0 flex-col">
        {CwFPagination(
          totalPeople,
          peoplePageNumber,
          handlePageChange,
          null,
          undefined,
          { disabled: isLoading }
        )}
        {renderListContent()}
        {CwFPagination(
          totalPeople,
          peoplePageNumber,
          handlePageChange,
          null,
          undefined,
          { disabled: isLoading }
        )}
      </Paper>
    </div>
  );
};

PeopleList.propTypes = {
  people: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  onAction: PropTypes.func.isRequired,
  getMenu: PropTypes.func.isRequired,
  totalPeople: PropTypes.number.isRequired,
  peoplePageNumber: PropTypes.number.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  sortBy: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func
};

export default PeopleList;
